import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'react-toastify';
import './AdminComponents.css';

const BookingRequestsManagement = () => {
  const { currentUser } = useAuth();
  const [bookingRequests, setBookingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [formData, setFormData] = useState({
    action: '',
    agentNotes: ''
  });
  const [filterStatus, setFilterStatus] = useState('pending_agent'); // Default filter for pending requests

  useEffect(() => {
    // Only load if user is an agent admin
    if (currentUser && currentUser.role === 'agent_admin') {
      loadBookingRequests();
    }
  }, [currentUser, filterStatus]);

  const loadBookingRequests = async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };
      
      let endpoint = '/api/bookings/agent';
      
      // If filtering, add query parameter
      if (filterStatus !== 'all') {
        endpoint = `/api/bookings/agent?status=${filterStatus}`;
      }
      
      const res = await axios.get(endpoint, config);
      setBookingRequests(res.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching booking requests:', err);
      setError('Failed to load booking requests');
      setLoading(false);
    }
  };

  const handleAction = (request) => {
    setSelectedRequest(request);
    setFormData({
      action: '',
      agentNotes: ''
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFilterChange = (status) => {
    setFilterStatus(status);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.action) {
      toast.error('Please select an action (approve or reject)');
      return;
    }
    
    try {
      setLoading(true);
      
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };
      
      const requestData = {
        action: formData.action,
        agentNotes: formData.agentNotes
      };
      
      await axios.put(
        `/api/bookings/${selectedRequest._id}/agent-response`, 
        requestData, 
        config
      );
      
      // Show success message based on action
      if (formData.action === 'approve') {
        toast.success('Booking request approved and forwarded to admin');
      } else {
        toast.info('Booking request rejected');
      }
      
      // Reset form and reload requests
      setSelectedRequest(null);
      setFormData({ action: '', agentNotes: '' });
      loadBookingRequests();
    } catch (err) {
      console.error('Error processing booking request:', err);
      toast.error(err.response?.data?.message || 'Failed to process request');
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'pending_agent':
        return <span className="badge pending">Pending Your Approval</span>;
      case 'pending_admin':
        return <span className="badge forwarded">Forwarded to Admin</span>;
      case 'approved':
        return <span className="badge approved">Approved</span>;
      case 'rejected':
      case 'rejected_by_agent':
        return <span className="badge rejected">Rejected</span>;
      case 'completed':
        return <span className="badge completed">Completed</span>;
      case 'cancelled':
        return <span className="badge cancelled">Cancelled</span>;
      default:
        return <span className="badge">{status}</span>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && !error) {
    return <div className="loading">Loading booking requests...</div>;
  }

  return (
    <div className="admin-component">
      <h2>Booking Requests Management</h2>
      
      {error && <div className="error-message">{error}</div>}
      
      {/* Status filter buttons */}
      <div className="filter-buttons">
        <button 
          className={`filter-btn ${filterStatus === 'all' ? 'active' : ''}`}
          onClick={() => handleFilterChange('all')}
        >
          All Requests
        </button>
        <button 
          className={`filter-btn ${filterStatus === 'pending_agent' ? 'active' : ''}`}
          onClick={() => handleFilterChange('pending_agent')}
        >
          Pending Approval
        </button>
        <button 
          className={`filter-btn ${filterStatus === 'pending_admin' ? 'active' : ''}`}
          onClick={() => handleFilterChange('pending_admin')}
        >
          Forwarded to Admin
        </button>
        <button 
          className={`filter-btn ${filterStatus === 'approved' ? 'active' : ''}`}
          onClick={() => handleFilterChange('approved')}
        >
          Approved
        </button>
        <button 
          className={`filter-btn ${filterStatus === 'rejected_by_agent' ? 'active' : ''}`}
          onClick={() => handleFilterChange('rejected_by_agent')}
        >
          Rejected
        </button>
      </div>
      
      {/* List of booking requests */}
      {bookingRequests.length === 0 ? (
        <div className="no-data">No booking requests found</div>
      ) : (
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>User</th>
                <th>Property</th>
                <th>Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {bookingRequests.map(request => (
                <tr key={request._id}>
                  <td>
                    <div className="user-info">
                      <div className="user-name">{request.user?.name || 'N/A'}</div>
                      <div className="user-email">{request.user?.email || 'N/A'}</div>
                    </div>
                  </td>
                  <td>
                    {request.lot && typeof request.lot === 'object' && request.lot.lotNumber ? (
                      <div className="property-info">
                        <div>Lot {request.lot.lotNumber}</div>
                        <div>{request.lot.type}</div>
                      </div>
                    ) : request.unit && typeof request.unit === 'object' && request.unit.unitNumber ? (
                      <div className="property-info">
                        <div>Unit {request.unit.unitNumber}</div>
                        <div>{request.unit.type}</div>
                      </div>
                    ) : request.specialLotId ? (
                      <div className="property-info">
                        <div>Special Lot ID: {request.specialLotId}</div>
                        {request.specialLotType && <div>{request.specialLotType}</div>}
                        {request.specialLotNumber && <div>Number: {request.specialLotNumber}</div>}
                      </div>
                    ) : (
                      'N/A'
                    )}
                  </td>
                  <td>{formatDate(request.requestDate)}</td>
                  <td>{getStatusBadge(request.status)}</td>
                  <td>
                    {request.status === 'pending_agent' && (
                      <button 
                        className="action-button"
                        onClick={() => handleAction(request)}
                      >
                        Process
                      </button>
                    )}
                    {request.status !== 'pending_agent' && (
                      <button 
                        className="view-button"
                        onClick={() => handleAction(request)}
                      >
                        View Details
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Action modal for selected request */}
      {selectedRequest && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Booking Request Details</h3>
            
            <div className="request-details">
              <div className="detail-group">
                <h4>User Information</h4>
                <p><strong>Name:</strong> {selectedRequest.user?.name || 'N/A'}</p>
                <p><strong>Email:</strong> {selectedRequest.user?.email || 'N/A'}</p>
                <p><strong>Phone:</strong> {selectedRequest.user?.phone || 'N/A'}</p>
              </div>
              
              <div className="detail-group">
                <h4>Property Information</h4>
                {selectedRequest.lot ? (
                  <>
                    <p><strong>Type:</strong> Lot</p>
                    <p><strong>Number:</strong> {selectedRequest.lot.lotNumber}</p>
                    <p><strong>Category:</strong> {selectedRequest.lot.type}</p>
                    <p><strong>Size:</strong> {selectedRequest.lot.buildUp || selectedRequest.lot.landSize} sqft</p>
                    <p><strong>Price:</strong> ₹{selectedRequest.lot.price}</p>
                  </>
                ) : selectedRequest.unit ? (
                  <>
                    <p><strong>Type:</strong> Unit</p>
                    <p><strong>Number:</strong> {selectedRequest.unit.unitNumber}</p>
                    <p><strong>Category:</strong> {selectedRequest.unit.type}</p>
                    <p><strong>Size:</strong> {selectedRequest.unit.buildUp || selectedRequest.unit.landSize} sqft</p>
                    <p><strong>Price:</strong> ₹{selectedRequest.unit.price}</p>
                  </>
                ) : (
                  <p>No property information available</p>
                )}
              </div>
              
              <div className="detail-group">
                <h4>Request Information</h4>
                <p><strong>Date:</strong> {formatDate(selectedRequest.requestDate)}</p>
                <p><strong>Status:</strong> {selectedRequest.status}</p>
                <p><strong>Message:</strong> {selectedRequest.requestMessage || 'No message provided'}</p>
                
                {selectedRequest.agentNotes && (
                  <p><strong>Agent Notes:</strong> {selectedRequest.agentNotes}</p>
                )}
                
                {selectedRequest.adminNotes && (
                  <p><strong>Admin Notes:</strong> {selectedRequest.adminNotes}</p>
                )}
              </div>
            </div>
            
            {/* Action form for pending requests */}
            {selectedRequest.status === 'pending_agent' && (
              <form onSubmit={handleSubmit} className="action-form">
                <div className="form-group">
                  <label>Action:</label>
                  <div className="radio-group">
                    <label className="radio-label">
                      <input 
                        type="radio" 
                        name="action" 
                        value="approve" 
                        checked={formData.action === 'approve'}
                        onChange={handleChange}
                      />
                      Approve & Forward to Admin
                    </label>
                    <label className="radio-label">
                      <input 
                        type="radio" 
                        name="action" 
                        value="reject" 
                        checked={formData.action === 'reject'}
                        onChange={handleChange}
                      />
                      Reject
                    </label>
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="agentNotes">Notes:</label>
                  <textarea
                    id="agentNotes"
                    name="agentNotes"
                    value={formData.agentNotes}
                    onChange={handleChange}
                    placeholder="Add any notes or comments about this request"
                    rows="4"
                  />
                </div>
                
                <div className="modal-actions">
                  <button 
                    type="button" 
                    className="cancel-button"
                    onClick={() => setSelectedRequest(null)}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="submit-button"
                    disabled={!formData.action}
                  >
                    Submit
                  </button>
                </div>
              </form>
            )}
            
            {/* Just a close button for non-pending requests */}
            {selectedRequest.status !== 'pending_agent' && (
              <div className="modal-actions">
                <button 
                  type="button" 
                  className="close-button"
                  onClick={() => setSelectedRequest(null)}
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingRequestsManagement; 